import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, X, Star, Trash2, PenLine, Quote } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("mv_admin_token")}` },
});

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {} = create, {id,...} = edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reviews`, authHeaders());
      setReviews(r.data);
    } catch { toast.error("Errore caricamento recensioni"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, authHeaders());
      toast.success("Recensione eliminata");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#FF5200]" size={32} /></div>;

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-wide">Recensioni</h1>
          <p className="text-sm text-zinc-500">
            {reviews.length} {reviews.length === 1 ? "recensione" : "recensioni"} pubblicate sul sito (sezione "Cosa dicono di noi")
          </p>
        </div>
        <button onClick={() => setEditing({})} data-testid="new-review-btn"
          className="flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2.5 rounded-sm text-sm font-bold uppercase hover:bg-[#e64a00] transition-colors">
          <Plus size={16} /> Nuova Recensione
        </button>
      </div>

      {reviews.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center text-zinc-400">
          <Quote size={32} className="mx-auto mb-3 text-zinc-300" />
          Nessuna recensione. Aggiungi la prima cliccando su <strong className="text-[#FF5200]">"Nuova Recensione"</strong>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((r, i) => (
            <div key={r.id} className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col" data-testid={`review-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} size={14} className={j < r.rating ? "text-[#FF5200] fill-[#FF5200]" : "text-zinc-200"} />
                  ))}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(r)} data-testid={`edit-review-${i}`}
                    className="p-1.5 text-zinc-400 hover:text-[#FF5200]" title="Modifica">
                    <PenLine size={14} />
                  </button>
                  <button onClick={() => onDelete(r.id)} data-testid={`delete-review-${i}`}
                    className="p-1.5 text-zinc-400 hover:text-red-500" title="Elimina">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-zinc-600 leading-relaxed mb-4 italic flex-1">&ldquo;{r.text}&rdquo;</p>
              <div className="border-t border-zinc-100 pt-3">
                <p className="font-bold text-sm text-[#09090B]">{r.author}</p>
                {r.company && <p className="text-xs text-zinc-500">{r.company}</p>}
                {r.created_at && <p className="text-[10px] text-zinc-400 mt-1 font-mono">{new Date(r.created_at).toLocaleDateString("it-IT")}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && <ReviewForm review={editing} onClose={() => setEditing(null)} onSave={load} />}
    </div>
  );
}


function ReviewForm({ review, onClose, onSave }) {
  const isEdit = !!review.id;
  const [form, setForm] = useState({
    author: review.author || "",
    company: review.company || "",
    rating: review.rating || 5,
    text: review.text || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.author.trim() || !form.text.trim()) {
      toast.error("Compila autore e testo");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/reviews/${review.id}`, form, authHeaders());
        toast.success("Recensione aggiornata");
      } else {
        await axios.post(`${API}/admin/reviews`, form, authHeaders());
        toast.success("Recensione creata");
      }
      onClose();
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-12 overflow-y-auto">
      <div className="bg-white rounded-sm w-full max-w-lg shadow-xl m-4" data-testid="review-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-heading font-bold text-lg uppercase tracking-wide">
            {isEdit ? "Modifica Recensione" : "Nuova Recensione"}
          </h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Autore *</label>
              <input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })}
                placeholder="Mario Rossi" data-testid="review-author"
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Azienda</label>
              <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                placeholder="ACME Srl" data-testid="review-company"
                className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Voto *</label>
            <div className="flex gap-1" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}
                  className="p-1" data-testid={`star-${n}`}>
                  <Star size={28} className={n <= form.rating ? "text-[#FF5200] fill-[#FF5200]" : "text-zinc-200 hover:text-[#FF5200]"} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Testo recensione *</label>
            <textarea value={form.text} onChange={e => setForm({ ...form, text: e.target.value })}
              placeholder="Servizio puntuale e professionale..." rows="5" data-testid="review-text"
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm resize-none" />
            <p className="text-[10px] text-zinc-400 mt-1">{form.text.length} caratteri</p>
          </div>

          <button type="submit" disabled={saving} data-testid="review-save-btn"
            className="w-full bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {isEdit ? "Salva Modifiche" : "Pubblica Recensione"}
          </button>
        </form>
      </div>
    </div>
  );
}
